import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { complianceApi, type EWayBillRecord } from '@/api/compliance.api';
import { mockSalesInvoices } from '@/mocks/billing';
import { mockEWayBills } from '@/mocks/ewaybill';
import DashboardTab from './components/DashboardTab';
import ExtendSlideOver from './components/ExtendSlideOver';
import BulkGenerateModal from './components/BulkGenerateModal';
import PrintEWayBillView from './components/PrintEWayBillView';
import UpdateVehicleSlideOver from './components/UpdateVehicleSlideOver';
import UpdateTransporterModal from './components/UpdateTransporterModal';
import { GetEWayBillDetailsModal } from './components/GetEWayBillDetailsModal';
import { calculatePincodeDistance } from '@/utils/pincodeDistance';

import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';

// ─── Pure utility functions — outside component ───────────────────────────────

function formatINR(n?: number | null): string {
  if (n == null || isNaN(Number(n))) return '₹0';
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

function timeRemaining(validUntil: string): { text: string; urgent: boolean; expired: boolean } {
  const now = Date.now();
  const end = new Date(validUntil).getTime();
  const diff = end - now;

  if (diff <= 0) {
    const days = Math.floor(Math.abs(diff) / (1000 * 60 * 60 * 24));
    const hrs = Math.floor((Math.abs(diff) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return { text: `Expired ${days} day${days > 1 ? 's' : ''} ago`, urgent: false, expired: true };
    return { text: `Expired ${hrs} hr${hrs !== 1 ? 's' : ''} ago`, urgent: false, expired: true };
  }

  const hrs = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hrs > 24) {
    const days = Math.floor(hrs / 24);
    return { text: `${days} day${days > 1 ? 's' : ''} remaining`, urgent: false, expired: false };
  }
  if (hrs > 0) return { text: `${hrs} hr ${mins} min remaining`, urgent: hrs < 6, expired: false };
  return { text: `${mins} min remaining`, urgent: true, expired: false };
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'ACTIVE':   return 'bg-emerald-100 text-emerald-700';
    case 'EXPIRED':  return 'bg-red-100 text-red-700';
    case 'CANCELLED':return 'bg-gray-100 text-gray-600';
    case 'EXTENDED': return 'bg-blue-100 text-blue-700';
    default:         return 'bg-gray-100 text-gray-600';
  }
}

function getStatusLabel(status: string): string {
  const map: Record<string, string> = { ACTIVE: 'Active', EXPIRED: 'Expired', CANCELLED: 'Cancelled', EXTENDED: 'Extended' };
  return map[status] || status;
}

function getVehicleTypeLabel(type: string): string {
  const map: Record<string, string> = { ROAD: 'Road', RAIL: 'Rail', AIR: 'Air', SHIP: 'Ship', '1': 'Road', '2': 'Rail', '3': 'Air', '4': 'Ship' };
  return map[type] || type;
}

// ✅ Extract state code from GSTIN first 2 digits
// e.g. "27AAGCB1286Q005" → 27 (Maharashtra), "07..." → 7 (Delhi)
function getStateCodeFromGSTIN(gstin: string): number {
  if (!gstin || gstin.length < 2 || gstin === 'URP') return 0;
  return parseInt(gstin.substring(0, 2), 10);
}

// ✅ Extract 6-digit pincode from address string
function extractPincode(address: string): number {
  const match = (address || '').match(/\b(\d{6})\b/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Extract a clean city/town name from an address string for the NIC toPlace field.
 * NIC requires a short city name (no commas, no pincodes, no house numbers).
 * Strategy: split by comma, filter out tokens that are:
 *   - 6-digit pincodes  - purely numeric  - very short (<3 chars)
 *   - contain digits mixed with text (house numbers like "#42", "No.5")
 *   - look like state names (long tokens at the end after pincode context)
 * Return the last clean token that looks like a city.
 */
function extractCity(address: string): string {
  if (!address) return '';
  // Remove pincode and trailing digits
  const cleaned = address.replace(/\b\d{6}\b/g, '').trim();
  const parts = cleaned.split(',').map(s => s.trim()).filter(Boolean);

  // Filter out parts that are clearly not a city:
  // - purely numeric, or start with # / No. / Plot
  // - contain digits (e.g. "Beml Nagar 560066", "Phase 2")
  // - very short tokens (initials, codes)
  const cityTokens = parts.filter(p => {
    if (p.length < 3) return false;               // too short
    if (/^\d+$/.test(p)) return false;            // purely numeric
    if (/^[#\d]/.test(p)) return false;           // starts with # or digit
    if (/\d/.test(p)) return false;               // contains any digit ("Phase 2", "Sector 14")
    return true;
  });

  // Prefer second-to-last city token (usually city before state)
  if (cityTokens.length >= 2) return cityTokens[cityTokens.length - 2];
  if (cityTokens.length === 1) return cityTokens[0];
  // Final fallback — second-to-last raw part
  return parts[parts.length - 2] || parts[0] || '';
}

// ✅ NIC Cancel reason codes
const cancelReasons = [
  { code: 1, label: 'Duplicate' },
  { code: 2, label: 'Order Cancelled' },
  { code: 3, label: 'Data Entry Mistake' },
  { code: 4, label: 'Others' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function EWayBillPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [activeTab, setActiveTab]               = useState<'all' | 'dashboard'>('all');
  const [searchQuery, setSearchQuery]           = useState('');
  const [statusFilter, setStatusFilter]         = useState<string>('ALL');
  const [dateFrom, setDateFrom]                 = useState('');
  const [dateTo, setDateTo]                     = useState('');
  const [selectedEWB, setSelectedEWB]           = useState<EWayBillRecord | null>(null);
  const [showDetailSlideOver, setShowDetailSlideOver]   = useState(false);
  const [showGenerateSlideOver, setShowGenerateSlideOver] = useState(false);
  const [showCancelModal, setShowCancelModal]   = useState(false);
  const [cancelTarget, setCancelTarget]         = useState<EWayBillRecord | null>(null);
  const [cancelRsnCode, setCancelRsnCode]       = useState<number>(0);
  const [cancelOtherRemark, setCancelOtherRemark] = useState('');
  const [isGenerating, setIsGenerating]         = useState(false);
  const [isCancelling, setIsCancelling]         = useState(false);

  // Live EWBs
  const [eWayBillsList, setEWayBillsList] = useState<EWayBillRecord[]>([]);

  const fetchEWayBills = async () => {
    try {
      const res = await complianceApi.getEWayBills({ status: statusFilter, search: searchQuery, dateFrom, dateTo });
      if (res?.success && Array.isArray(res.data)) {
        setEWayBillsList(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch E-Way Bills:', err);
    }
  };

  useEffect(() => { fetchEWayBills(); }, [statusFilter, searchQuery, dateFrom, dateTo]);

  const [extendTarget, setExtendTarget] = useState<EWayBillRecord | null>(null);
  const [updateVehicleTarget, setUpdateVehicleTarget] = useState<EWayBillRecord | null>(null);
  const [updateTransporterTarget, setUpdateTransporterTarget] = useState<EWayBillRecord | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [printEWB, setPrintEWB]         = useState<EWayBillRecord | null>(null);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [lookupEwbNo, setLookupEwbNo]   = useState('');
  const [lookupSupplierGstin, setLookupSupplierGstin] = useState('');

  // ── Generate form state ────────────────────────────────────────────────────
  const [genSourceType, setGenSourceType]       = useState<'invoice' | 'challan'>('invoice');
  const [genSelectedInvoice, setGenSelectedInvoice] = useState('');
  const [genVehicleNumber, setGenVehicleNumber] = useState('');
  const [genTransporterName, setGenTransporterName] = useState('');
  const [genTransporterId, setGenTransporterId] = useState('');
  const [genDistance, setGenDistance]           = useState('');
  const [genTransportMode, setGenTransportMode] = useState('1');
  const [genFromPincode, setGenFromPincode]     = useState('400001'); // Dispatch Pincode
  const [genToGSTIN, setGenToGSTIN]             = useState('');
  const [genToAddress, setGenToAddress]         = useState('');
  const [genToPincode, setGenToPincode]         = useState('');   // ✅ new
  const [genToStateCode, setGenToStateCode]     = useState('');   // ✅ new
  const [genToPlace, setGenToPlace]             = useState('');   // ✅ new

  // Global active warehouse state from top header bar
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();

  // Doc search
  const [docSearchQuery, setDocSearchQuery]         = useState('');
  const [fetchedInvoices, setFetchedInvoices]       = useState<any[]>([]);
  const [fetchedChallans, setFetchedChallans]       = useState<any[]>([]);
  const [isSearchingDocs, setIsSearchingDocs]       = useState(false);
  const [docDropdownOpen, setDocDropdownOpen]       = useState(false);
  // ✅ Full invoice detail (with items) fetched on selection
  const [selectedInvoiceFull, setSelectedInvoiceFull] = useState<any>(null);
  const [isFetchingInvoice, setIsFetchingInvoice]   = useState(false);

  // Prefilled from navigation
  const [prefilledInvoiceId, setPrefilledInvoiceId]       = useState<string | null>(null);
  const [prefilledInvoiceNumber, setPrefilledInvoiceNumber] = useState<string | null>(null);

  useEffect(() => {
    if (!showGenerateSlideOver) return;
    const loadDocs = async () => {
      setIsSearchingDocs(true);
      try {
        if (genSourceType === 'invoice') {
          // Pass selectedWarehouseId and pendingEwbOnly = true
          const res = await complianceApi.searchInvoices(docSearchQuery, selectedWarehouseId, true);
          const items = res?.data?.items || res?.data;
          if (Array.isArray(items)) {
            // Filter out invoices that are CANCELLED, <= ₹50,000 (EWB not required), or already have an active EWB
            const ungeneratedInvoices = items.filter((inv: any) => {
              if (inv.status === 'CANCELLED') return false;
              const total = Number(inv.grandTotal || inv.total_amount || inv.totalAmount || 0);
              if (total <= 50000) return false; // E-Way Bill is only required for consignment value > 50,000
              if (inv.has_eway_bill || inv.hasEwayBill) return false;
              const invNo = inv.invoiceNo || inv.invoice_number || inv.billNo;
              if (eWayBillsList.some(e => e.status !== 'CANCELLED' && (e.invoiceId === inv.id || e.invoiceNumber === invNo))) {
                return false;
              }
              return true;
            });
            setFetchedInvoices(ungeneratedInvoices);
          }
        } else {
          const res = await complianceApi.searchChallans(docSearchQuery, selectedWarehouseId);
          const items = res?.data?.items || res?.data;
          if (Array.isArray(items)) setFetchedChallans(items);
        }
      } catch (err) {
        console.error('Failed to load docs:', err);
      } finally {
        setIsSearchingDocs(false);
      }
    };
    const t = setTimeout(loadDocs, 250);
    return () => clearTimeout(t);
  }, [genSourceType, docSearchQuery, selectedWarehouseId, showGenerateSlideOver, eWayBillsList]);

  // Auto-trigger from navigation
  useEffect(() => {
    const state = location.state as any;
    if (state?.autoGenerate) {
      setPrefilledInvoiceId(state.invoiceId || null);
      setPrefilledInvoiceNumber(state.invoiceNumber || null);
      setShowGenerateSlideOver(true);
      if (state.invoiceNumber) { setGenSelectedInvoice(state.invoiceNumber); setGenSourceType('invoice'); }
      navigate(location.pathname, { replace: true, state: null });
    } else if (state?.viewInvoiceNumber || state?.searchQuery) {
      const target = state.viewInvoiceNumber || state.searchQuery || '';
      setSearchQuery(target);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [eWayBillsList]);

  // ── Computed ───────────────────────────────────────────────────────────────
  const filteredEWBs = useMemo(() => {
    let list = [...eWayBillsList];
    if (statusFilter !== 'ALL') list = list.filter(e => e.status === statusFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        e.ewbNumber?.toLowerCase().includes(q) ||
        e.invoiceNumber?.toLowerCase().includes(q) ||
        e.customerName?.toLowerCase().includes(q) ||
        e.vehicleNumber?.toLowerCase().includes(q)
      );
    }
    if (dateFrom) list = list.filter(e => new Date(e.generatedDate) >= new Date(dateFrom));
    if (dateTo)   list = list.filter(e => new Date(e.generatedDate) <= new Date(dateTo + 'T23:59:59'));
    return list.sort((a, b) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime());
  }, [eWayBillsList, searchQuery, statusFilter, dateFrom, dateTo]);

  const summary = useMemo(() => ({
    active:        eWayBillsList.filter(e => e.status === 'ACTIVE').length,
    expiringToday: eWayBillsList.filter(e => {
      if (e.status !== 'ACTIVE') return false;
      const diff = new Date(e.validUntil).getTime() - Date.now();
      return diff > 0 && diff <= 6 * 60 * 60 * 1000;
    }).length,
    expired:   eWayBillsList.filter(e => e.status === 'EXPIRED').length,
    cancelled: eWayBillsList.filter(e => e.status === 'CANCELLED').length,
  }), [eWayBillsList]);

  const expiringEWBs = useMemo(() =>
    eWayBillsList.filter(e => {
      if (e.status !== 'ACTIVE') return false;
      const diff = new Date(e.validUntil).getTime() - Date.now();
      return diff > 0 && diff <= 6 * 60 * 60 * 1000;
    }), [eWayBillsList]);

  // ✅ Selected invoice data — now includes pincode, stateCode, place, items
  const selectedInvoiceData = useMemo(() => {
    if (!genSelectedInvoice) return null;

    // ✅ Priority 1: Use full invoice detail (with items) if fetched
    if (selectedInvoiceFull && genSourceType === 'invoice') {
      const inv = selectedInvoiceFull;
      const gstin    = inv.customerGstin || '';
      const address  = inv.billingAddress || inv.shippingAddress || '';
      const pincode  = extractPincode(address) || Number(inv.billingPincode || 0);
      const stateCode = getStateCodeFromGSTIN(gstin) || Number(inv.stateCode || 0);
      const addrParts = address.split(',').map((s: string) => s.trim()).filter(Boolean);
      return {
        id:            inv.id,
        partyName:     inv.partyName || 'Customer',
        customerGstin: gstin,
        grandTotal:    Number(inv.grandTotal || 0),
        billingAddress: address,
        pincode,
        stateCode,
        place:         addrParts[addrParts.length - 2] || addrParts[0] || '',
        addr1:         addrParts[0] || address,
        addr2:         addrParts[1] || '',
        itemCount:     Array.isArray(inv.items) ? inv.items.length : 1,
        items:         Array.isArray(inv.items) ? inv.items : [],  // ✅ actual items!
      };
    }

    if (genSourceType === 'invoice') {
      const live = fetchedInvoices.find(inv =>
        inv.invoiceNo === genSelectedInvoice ||
        inv.invoice_number === genSelectedInvoice ||
        inv.id === genSelectedInvoice
      );
      if (live) {
        const gstin    = live.customerGstin || live.customer_gstin || live.gstin || '';
        const address  = live.billingAddress || live.billing_address || live.address || '';
        const pincode  = extractPincode(address) || Number(live.billingPincode || live.pincode || 0);
        const stateCode = getStateCodeFromGSTIN(gstin) || Number(live.stateCode || live.state_code || 0);
        const addrParts = address.split(',').map((s: string) => s.trim()).filter(Boolean);
        return {
          id:            live.id,
          partyName:     live.partyName || live.customer_name || live.party_name || 'Customer',
          customerGstin: gstin,
          grandTotal:    Number(live.grandTotal || live.total_amount || live.totalAmount || 0),
          billingAddress: address,
          pincode,
          stateCode,
          place:         addrParts[addrParts.length - 2] || addrParts[0] || '',
          addr1:         addrParts[0] || address,
          addr2:         addrParts[1] || '',
          itemCount:     Array.isArray(live.items) ? live.items.length : 1,
          items:         Array.isArray(live.items) ? live.items : [],
        };
      }
      // Mock fallback
      const mock = mockSalesInvoices.find(si => si.billNo === genSelectedInvoice);
      if (mock) {
        const address   = mock.billingAddress || '';
        const pincode   = extractPincode(address);
        const stateCode = getStateCodeFromGSTIN(mock.customerGstin || '');
        return {
          id: mock.id, partyName: mock.partyName,
          customerGstin: mock.customerGstin || '',
          grandTotal: mock.grandTotal, billingAddress: address,
          pincode, stateCode, place: '', addr1: address, addr2: '',
          itemCount: mock.itemCount, items: [],
        };
      }
    } else {
      // ✅ Priority 1: Full challan detail with items (fetched via getChallanById)
      if (selectedInvoiceFull && genSourceType === 'challan') {
        const ch = selectedInvoiceFull;
        const gstin    = ch.customerGstin || ch.toGSTIN || '';
        const address  = ch.deliveryAddress || ch.billingAddress || '';
        const pincode  = extractPincode(address) || Number(ch.billingPincode || 0);
        const stateCode = getStateCodeFromGSTIN(gstin) || Number(ch.stateCode || 0);
        const addrParts = address.split(',').map((s: string) => s.trim()).filter(Boolean);
        return {
          id:            ch.id,
          partyName:     ch.partyName || 'Customer',
          customerGstin: gstin,
          grandTotal:    Number(ch.grandTotal || ch.totalValue || 0),
          billingAddress: address,
          pincode,
          stateCode,
          place:         addrParts[addrParts.length - 2] || addrParts[0] || '',
          addr1:         addrParts[0] || address,
          addr2:         addrParts[1] || '',
          itemCount:     Array.isArray(ch.items) ? ch.items.length : 1,
          items:         Array.isArray(ch.items) ? ch.items : [], // ✅ actual challan items with HSN
        };
      }

      const liveCh = fetchedChallans.find(c =>
        c.challanNumber === genSelectedInvoice ||
        c.challan_number === genSelectedInvoice ||
        c.id === genSelectedInvoice
      );
      if (liveCh) {
        const gstin    = liveCh.customerGstin || liveCh.toGSTIN || '';
        const address  = liveCh.deliveryAddress || liveCh.toAddress || '';
        const pincode  = extractPincode(address);
        const stateCode = getStateCodeFromGSTIN(gstin);
        return {
          id: liveCh.id,
          partyName: liveCh.customerName || liveCh.partyName || 'Customer',
          customerGstin: gstin,
          grandTotal: Number(liveCh.totalValue || liveCh.total_amount || 0),
          billingAddress: address, pincode, stateCode,
          place: '', addr1: address, addr2: '',
          itemCount: Array.isArray(liveCh.items) ? liveCh.items.length : 1,
          items: Array.isArray(liveCh.items) ? liveCh.items : [],
        };
      }
    }
    return null;
  }, [genSelectedInvoice, genSourceType, fetchedInvoices, fetchedChallans, selectedInvoiceFull]); // ✅ selectedInvoiceFull must be here

  const distanceCalc = useMemo(() => {
    if (!genToPincode || genToPincode.length < 3) return null;
    return calculatePincodeDistance(genFromPincode || '400001', genToPincode);
  }, [genFromPincode, genToPincode]);

  useEffect(() => {
    if (distanceCalc && distanceCalc.distanceKm > 0) {
      setGenDistance(String(distanceCalc.distanceKm));
    }
  }, [distanceCalc]);

  const estimatedValidityDays = useMemo(() => {
    const km = parseInt(genDistance, 10);
    if (isNaN(km) || km <= 0) return 1;
    return Math.max(1, Math.ceil(km / 200));
  }, [genDistance]);

  const canCancel = useCallback((ewb: EWayBillRecord) => {
    if (ewb.status !== 'ACTIVE') return false;
    return Date.now() - new Date(ewb.generatedDate).getTime() <= 24 * 60 * 60 * 1000;
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleViewDetail  = (ewb: EWayBillRecord) => { setSelectedEWB(ewb); setShowDetailSlideOver(true); };
  const handleExtendClick = (ewb: EWayBillRecord) => setExtendTarget(ewb);
  const handleExtendClosed = () => setExtendTarget(null);
  const handleExtended    = () => { setExtendTarget(null); toast.success('E-Way Bill extended successfully'); fetchEWayBills(); };
  const handleVehicleUpdated = () => { setUpdateVehicleTarget(null); fetchEWayBills(); };
  const handleTransporterUpdated = () => { setUpdateTransporterTarget(null); fetchEWayBills(); };
  const handlePrintEWB    = (ewb: EWayBillRecord) => setPrintEWB(ewb);
  const handleCopyEWB     = (n: string) => {
    navigator.clipboard.writeText(n)
      .then(() => toast.success('E-Way Bill number copied!'))
      .catch(() => toast.error('Failed to copy E-Way Bill number'));
  };
  const handleExpiringFilter = () => { setStatusFilter('ACTIVE'); setSearchQuery(''); setDateFrom(''); setDateTo(''); };
  const handleBulkGenerated  = () => {
    setShowBulkModal(false);
    fetchEWayBills();
  };

  const handleExportCSV = () => {
    const listToExport = filteredEWBs.length > 0 ? filteredEWBs : eWayBillsList;
    if (!listToExport || listToExport.length === 0) {
      toast.info('No E-Way Bills available to export');
      return;
    }

    const headers = [
      'EWB Number',
      'Status',
      'Invoice / Doc No',
      'Generated Date',
      'Valid Until',
      'Customer Name',
      'Customer GSTIN',
      'Supplier Name',
      'Supplier GSTIN',
      'Dispatch Address',
      'Destination Address',
      'Transport Mode',
      'Vehicle Number',
      'Transporter ID',
      'Transporter Name',
      'Distance (KM)',
      'Taxable Value (INR)',
      'CGST (INR)',
      'SGST (INR)',
      'IGST (INR)',
      'Total Value (INR)',
      'Generated By',
    ];

    const escapeCSV = (val: any) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const rows = listToExport.map(ewb => [
      escapeCSV(ewb.ewbNumber),
      escapeCSV(ewb.status),
      escapeCSV(ewb.invoiceNumber),
      escapeCSV(ewb.generatedDate ? new Date(ewb.generatedDate).toLocaleDateString('en-IN') : ''),
      escapeCSV(ewb.validUntil ? new Date(ewb.validUntil).toLocaleString('en-IN') : ''),
      escapeCSV(ewb.customerName || ''),
      escapeCSV(ewb.toGSTIN || ''),
      escapeCSV(ewb.supplierName || ''),
      escapeCSV(ewb.fromGSTIN || ''),
      escapeCSV(ewb.fromAddress || ''),
      escapeCSV(ewb.toAddress || ''),
      escapeCSV(ewb.transportMode || ewb.vehicleType || 'Road'),
      escapeCSV(ewb.vehicleNumber || ''),
      escapeCSV(ewb.transporterId || ''),
      escapeCSV(ewb.transporterName || ''),
      escapeCSV(ewb.distanceKm || 0),
      escapeCSV(Number(ewb.totalTaxableValue || 0).toFixed(2)),
      escapeCSV(Number(ewb.cgst || 0).toFixed(2)),
      escapeCSV(Number(ewb.sgst || 0).toFixed(2)),
      escapeCSV(Number(ewb.igst || 0).toFixed(2)),
      escapeCSV(Number(ewb.totalValue || 0).toFixed(2)),
      escapeCSV(ewb.generatedBy || 'System'),
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EWayBills_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${listToExport.length} E-Way Bill record(s) to CSV ✓`);
  };

  const handleCancelClick = (ewb: EWayBillRecord) => {
    setCancelTarget(ewb);
    setCancelRsnCode(0);
    setCancelOtherRemark('');
    setShowCancelModal(true);
  };

  // ✅ Cancel with correct NIC payload
  const handleConfirmCancel = async () => {
    if (!cancelTarget || !cancelRsnCode) return;
    setIsCancelling(true);
    try {
      const remark = cancelRsnCode === 4 ? cancelOtherRemark : (cancelReasons.find(r => r.code === cancelRsnCode)?.label || '');
      const res = await complianceApi.cancelEWayBill({
        ewbNo:         cancelTarget.ewbNumber,   // ✅ 12-digit EWB number
        ewbId:         cancelTarget.id,           // DB row id
        cancelRsnCode,                            // ✅ NIC reason code
        cancelRmrk:    remark,
      } as any);
      if (res?.success) {
        toast.success(res?.message || 'E-Way Bill cancelled successfully');
        fetchEWayBills();
      } else {
        toast.error(res?.message || 'Cancellation failed');
      }
    } catch (err: any) {
      console.error('Cancel failed:', err);
      toast.error(err?.message || 'Failed to cancel E-Way Bill');
    } finally {
      setIsCancelling(false);
      setShowCancelModal(false);
      setCancelTarget(null);
    }
  };

  const resetGenerateForm = () => {
    setGenSelectedInvoice(''); setDocSearchQuery(''); setDocDropdownOpen(false);
    setGenVehicleNumber(''); setGenTransporterName(''); setGenTransporterId('');
    setGenDistance(''); setGenTransportMode('1');
    setGenToGSTIN(''); setGenToAddress(''); setGenToPincode('');
    setGenToStateCode(''); setGenToPlace('');
    setSelectedInvoiceFull(null);  // ✅ clear full invoice data
    setPrefilledInvoiceId(null); setPrefilledInvoiceNumber(null);
  };

  const openGenerateSlideOver = () => { resetGenerateForm(); setGenSourceType('invoice'); setShowGenerateSlideOver(true); };

  // ✅ Fetch full invoice detail (with items) when invoice is selected
  const fetchInvoiceDetail = async (invoiceId: string) => {
    if (!invoiceId) return;
    setIsFetchingInvoice(true);
    try {
      const res = await complianceApi.getInvoiceById(invoiceId);
      if (res?.success && res?.data) {
        const inv = res.data;
        const hasExistingEWB = inv.has_eway_bill || inv.hasEwayBill || eWayBillsList.some(e => e.status !== 'CANCELLED' && (e.invoiceId === inv.id || e.invoiceNumber === (inv.invoice_number || inv.invoiceNo)));
        if (hasExistingEWB) {
          toast.warning(`Invoice ${inv.invoice_number || inv.invoiceNo || invoiceId} already has an active E-Way Bill generated.`);
        }
        setSelectedInvoiceFull(inv);
        if (inv.customerGstin) setGenToGSTIN(inv.customerGstin);
        const addr = inv.billingAddress || inv.shippingAddress || '';
        if (addr) setGenToAddress(addr);
        const pincode = extractPincode(addr) || Number(inv.billingPincode || 0);
        const sc = getStateCodeFromGSTIN(inv.customerGstin || '');
        if (pincode) setGenToPincode(String(pincode));
        if (sc)      setGenToStateCode(String(sc));
        console.log('[EWB] Invoice detail fetched — items:', inv.items?.length);
      }
    } catch (err) {
      console.error('Failed to fetch invoice detail:', err);
    } finally {
      setIsFetchingInvoice(false);
    }
  };

  // ✅ Fetch full challan detail (with items + HSN) when challan is selected
  const fetchChallanDetail = async (challanId: string) => {
    if (!challanId) return;
    setIsFetchingInvoice(true);
    try {
      const res = await complianceApi.getChallanById(challanId);
      if (res?.success && res?.data) {
        setSelectedInvoiceFull(res.data);
        const ch = res.data;
        if (ch.customerGstin || ch.toGSTIN) setGenToGSTIN(ch.customerGstin || ch.toGSTIN || '');
        if (ch.deliveryAddress || ch.billingAddress) {
          setGenToAddress(ch.deliveryAddress || ch.billingAddress || '');
        }
        const addr    = ch.deliveryAddress || ch.billingAddress || '';
        const pincode = extractPincode(addr);
        const gstin   = ch.customerGstin || ch.toGSTIN || '';
        const sc      = getStateCodeFromGSTIN(gstin) || Number(ch.stateCode || 0);
        if (pincode) setGenToPincode(String(pincode));
        if (sc)      setGenToStateCode(String(sc));
      }
    } catch (err) {
      console.error('Failed to fetch challan detail:', err);
    } finally {
      setIsFetchingInvoice(false);
    }
  };

  // ✅ Fixed handleGenerateEWB — correct pincode, GST, distance
  const handleGenerateEWB = async () => {
    if (!genSelectedInvoice) return;
    if (genTransportMode === '1' && !genVehicleNumber.trim()) {
      toast.error('Vehicle Number is mandatory for Road transport');
      setIsGenerating(false);
      return;
    }
    setIsGenerating(true);
    try {
      // ── To (Customer) fields ────────────────────────────────────────────
      const customerGstin = genToGSTIN || selectedInvoiceData?.customerGstin || 'URP';

      // State code — from manual input → GSTIN prefix → selectedInvoiceData
      const toStateCode = genToStateCode
        ? Number(genToStateCode)
        : getStateCodeFromGSTIN(customerGstin) || selectedInvoiceData?.stateCode || 27;

      // Pincode — from manual input → address extraction → selectedInvoiceData
      const toAddressFull = genToAddress || selectedInvoiceData?.billingAddress || '';
      const toPincode = genToPincode
        ? Number(genToPincode)
        : extractPincode(toAddressFull) || selectedInvoiceData?.pincode || 400001;

      // Address parts
      const addrParts = toAddressFull.split(',').map((s: string) => s.trim()).filter(Boolean);
      const toAddr1   = addrParts[0] || 'Customer Address';
      const toAddr2   = addrParts[1] || '';
      const toPlace   = genToPlace || extractCity(toAddressFull) || 'City';

      // ── Items — from getSalesInvoiceById response ───────────────────────────
      // getSalesInvoiceById returns: itemName, hsnCode, qty, unit, taxableAmount, taxRate, cgst, sgst, igst
      const invoiceItems = selectedInvoiceData?.items || [];
      const itemList = invoiceItems.length > 0
        ? invoiceItems.map((item: any) => {
            const hsnRaw  = String(item.hsnCode || item.hsn_code || item.hsn || '').trim();
            // ✅ Min 4 digits required, pad to 6 if shorter, fallback to 999900
            const hsnCode = hsnRaw.length >= 4
              ? (hsnRaw.length >= 6 ? Number(hsnRaw) : Number(hsnRaw.padEnd(6, '0')))
              : 999900;

            const taxableAmt = Number(item.taxableAmount || item.taxable_amount || 0);

            // ✅ Derive tax rates from amounts (getSalesInvoiceById stores amounts not rates)
            // taxRate field IS available from DB — use it directly
            const taxRate = Number(item.taxRate || item.tax_rate || 18);

            // cgst/sgst/igst are amounts in DB — convert to rates
            // Or use taxRate to split: intrastate = cgst+sgst, interstate = igst
            const cgstAmt = Number(item.cgst || 0);
            const sgstAmt = Number(item.sgst || 0);
            const igstAmt = Number(item.igst || 0);

            // Derive rates from amounts (cgst = taxRate/2 for intrastate)
            const cgstRate = taxableAmt > 0 ? Number(((cgstAmt / taxableAmt) * 100).toFixed(2)) : 0;
            const sgstRate = taxableAmt > 0 ? Number(((sgstAmt / taxableAmt) * 100).toFixed(2)) : 0;
            const igstRate = taxableAmt > 0 ? Number(((igstAmt / taxableAmt) * 100).toFixed(2)) : 0;

            const qty  = Number(item.qty || item.quantity || 1);
            const rate = Number(item.rate || item.unitPrice || item.unit_price ||
                           (taxableAmt > 0 && qty > 0 ? taxableAmt / qty : 0));

            return {
              productName:   item.itemName || item.productName || item.name || 'Goods',
              productDesc:   item.itemName || item.productName || item.name || 'Goods',
              itemCode:      item.itemCode || item.item_code || item.sku || '',
              hsnCode,
              quantity:      qty,
              qtyUnit:       item.unit || item.qtyUnit || 'NOS',
              rate,
              taxableAmount: taxableAmt,
              // ✅ Use pre-derived rates if available (from getChallanById/getSalesInvoiceById)
              cgstRate:      Number(item.cgstRate || cgstRate || 0),
              sgstRate:      Number(item.sgstRate || sgstRate || 0),
              igstRate:      Number(item.igstRate || igstRate || 0),
              cessRate:      0,
            };
          })
        : [{
            // Fallback — no items found
            productName:   'General Goods',
            productDesc:   'General Goods',
            hsnCode:       999900,  // ✅ valid NIC fallback HSN
            quantity:      1,
            qtyUnit:       'NOS',
            taxableAmount: Number(selectedInvoiceData?.grandTotal || 50000),
            cgstRate:      0,
            sgstRate:      0,
            igstRate:      18,  // default interstate
            cessRate:      0,
          }];

      // Format document number safely within 16 chars by removing dashes
      const rawDocStr = String(genSelectedInvoice || '').trim();
      const docNo = rawDocStr.length <= 16
        ? rawDocStr
        : (rawDocStr.replace(/-/g, '').length <= 16
            ? rawDocStr.replace(/-/g, '')
            : rawDocStr.replace(/[^a-zA-Z0-9]/g, '').slice(-16));

      const payload = {
        // Document
        docNo,
        docType:       genSourceType === 'challan' ? 'CHL' : 'INV',
        supplyType:    'O',
        subSupplyType: '1',
        transactionType: 1,

        // To (Customer)
        toGstin:       customerGstin,
        toTrdName:     selectedInvoiceData?.partyName || 'Customer',
        toAddr1,
        toAddr2,
        toPlace,
        toPincode,           // ✅ extracted from address or manual
        toStateCode,         // ✅ from GSTIN prefix or manual
        actToStateCode: toStateCode,

        // Transport
        transMode:       genTransportMode || '1',
        transDistance:   '0',              // ✅ always 0 — NIC auto-calculates
        vehicleNo:       (genVehicleNumber || '').replace(/\s+/g, ''),
        vehicleType:     'R',
        transporterName: genTransporterName || '',
        transporterId:   genTransporterId || '',
        transDocNo:      '',
        transDocDate:    '',

        // ERP fields
        invoiceId:     selectedInvoiceData?.id || null,
        invoiceNumber: genSelectedInvoice,
        customerName:  selectedInvoiceData?.partyName || 'Customer',
        distanceKm:    genDistance && Number(genDistance) > 0 ? genDistance : (toStateCode === 27 ? '120' : '450'),

        // Items
        itemList,
      };

      const res = await complianceApi.generateEWayBill(payload);
      if (res?.success) {
        fetchEWayBills();
        toast.success(res?.message || `E-Way Bill Generated: ${res.data?.ewbNumber || res.data?.ewbNo || 'Success'}`);
        setShowGenerateSlideOver(false);
        resetGenerateForm();
      } else {
        console.error('EWB failed:', res);
        // ── Backend already maps NIC error codes into res.errors[] ─────────
        const nicErrors = res?.errors;
        if (nicErrors && nicErrors.length > 0) {
          nicErrors.forEach((e: any) => {
            toast.error(`[${e.code}] ${e.message} — ${e.fix}`);
          });
        } else {
          toast.error(res?.message || 'EWB generation failed');
        }
      }
    } catch (err: any) {
      console.error('Generate EWB error:', err);
      toast.error(err?.message || 'Something went wrong while generating E-Way Bill. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">E-Way Bill</h1>
            <p className="text-sm text-gray-500 mt-0.5">Electronic way bill for goods movement</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openGenerateSlideOver} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 cursor-pointer whitespace-nowrap">
              <i className="ri-add-line" /> Generate EWB
            </button>
            <button onClick={() => setShowBulkModal(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-stack-line" /> Bulk Generate
            </button>
            <button
              onClick={() => {
                setLookupEwbNo('');
                setLookupSupplierGstin('');
                setShowLookupModal(true);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 cursor-pointer whitespace-nowrap"
              title="Verify or Fetch E-Way Bill from Government NIC Portal"
            >
              <i className="ri-shield-check-line text-indigo-600" /> Verify from NIC
            </button>
            <button onClick={() => navigate('/compliance/eway-bill/settings')} className="inline-flex items-center gap-1 px-2.5 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer" title="Settings">
              <i className="ri-settings-3-line" />
            </button>
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              title="Export E-Way Bills to CSV"
            >
              <i className="ri-download-line" /> Export
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {(['all', 'dashboard'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md cursor-pointer whitespace-nowrap transition-colors ${activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab === 'all' ? 'All EWBs' : 'Dashboard'}
            </button>
          ))}
        </div>

        {activeTab === 'dashboard' && <DashboardTab ewbs={eWayBillsList} summary={summary} />}

        {activeTab === 'all' && (
          <>
            {/* Expiry Alert */}
            {expiringEWBs.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <i className="ri-alert-line text-amber-600 text-lg" />
                <span className="text-sm text-amber-800 flex-1"><strong>{expiringEWBs.length}</strong> E-Way Bill(s) expiring within 6 hours.</span>
                <button onClick={handleExpiringFilter} className="text-xs font-medium text-amber-700 underline cursor-pointer hover:text-amber-900 whitespace-nowrap">View Expiring EWBs</button>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Active EWBs', value: summary.active, color: 'bg-emerald-500', sub: 'Currently valid' },
                { label: 'Expiring Today', value: summary.expiringToday, color: 'bg-amber-500', sub: 'Needs attention' },
                { label: 'Expired', value: summary.expired, color: 'bg-red-500', sub: 'Past validity' },
                { label: 'Cancelled', value: summary.cancelled, color: 'bg-gray-400', sub: 'This month' },
              ].map(card => (
                <div key={card.label} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${card.color}`} />
                    <span className="text-xs text-gray-500">{card.label}</span>
                  </div>
                  <div className="text-2xl font-semibold text-gray-900">{card.value}</div>
                  <div className="mt-1 text-xs text-gray-500">{card.sub}</div>
                </div>
              ))}
            </div>

            {/* Filter Bar */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="relative flex-1 min-w-0">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input type="text" placeholder="Search EWB number, invoice, customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white">
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="EXTENDED">Extended</option>
                </select>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                <span className="text-gray-400 text-sm hidden sm:inline">to</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                <button onClick={() => { setSearchQuery(''); setStatusFilter('ALL'); setDateFrom(''); setDateTo(''); }}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-refresh-line" /> Reset
                </button>
              </div>
            </div>

            {/* EWB Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col h-[500px]">
              <div className="px-4 py-3 border-b border-gray-200 shrink-0"  >
                <h3 className="text-sm font-semibold text-gray-900">E-Way Bill Records</h3>
              </div>
              <div className="flex-1 min-h-0 overflow-auto hover-scrollbar bg-white">
                <table className="w-full min-w-[1200px] text-xs">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {['EWB Number', 'Invoice No', 'Customer', 'Distance', 'Generated', 'Valid Until', 'Status', 'Actions'].map(h => (
                        <th key={h} className={`px-3 py-2.5 font-medium text-gray-500 whitespace-nowrap ${h === 'Distance' ? 'text-right' : h === 'Status' || h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEWBs.length === 0 ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No E-Way Bills found</td></tr>
                    ) : filteredEWBs.map(ewb => {
                      const tr = timeRemaining(ewb.validUntil);
                      return (
                        <tr key={ewb.id} className="border-b border-slate-50 hover:bg-indigo-50/30 cursor-pointer transition-colors">
                          <td className="px-3 py-2.5 text-gray-900 font-mono font-medium whitespace-nowrap">{ewb.ewbNumber}</td>
                          <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{ewb.invoiceNumber}</td>
                          <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{ewb.customerName || '—'}</td>
                          <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap font-medium">
                            {ewb.distanceKm && Number(ewb.distanceKm) > 0 ? `${ewb.distanceKm} km` : '100 km'}
                          </td>
                          <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{formatDate(ewb.generatedDate)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="text-gray-600">{formatDateTime(ewb.validUntil)}</span>
                            {(ewb.status === 'ACTIVE' || ewb.status === 'EXTENDED') && (
                              <span className={`block text-[10px] mt-0.5 ${tr.urgent ? 'text-amber-600 font-medium' : tr.expired ? 'text-red-500' : 'text-gray-400'}`}>{tr.text}</span>
                            )}
                            {ewb.status === 'EXPIRED' && <span className="block text-[10px] mt-0.5 text-red-500">{tr.text}</span>}
                          </td>
                          <td className="px-3 py-2.5 text-center whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(ewb.status)}`}>{getStatusLabel(ewb.status)}</span>
                          </td>
                          <td className="px-3 py-2.5 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => handleViewDetail(ewb)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer">
                                <i className="ri-eye-line" /> View
                              </button>
                              {(ewb.status === 'ACTIVE' || ewb.status === 'EXTENDED') && (
                                <>
                                  <button
                                    onClick={() => setUpdateVehicleTarget(ewb)}
                                    className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 cursor-pointer"
                                    title="Update Vehicle / Part-B"
                                  >
                                    <i className="ri-truck-line" /> Vehicle
                                  </button>
                                  <button
                                    onClick={() => setUpdateTransporterTarget(ewb)}
                                    className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded hover:bg-amber-100 cursor-pointer"
                                    title="Update Transporter"
                                  >
                                    <i className="ri-user-shared-line" /> Transporter
                                  </button>
                                  <button onClick={() => handleExtendClick(ewb)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 cursor-pointer">
                                    <i className="ri-refresh-line" /> Extend
                                  </button>
                                </>
                              )}
                              {canCancel(ewb) && (
                                <button onClick={() => handleCancelClick(ewb)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 cursor-pointer">
                                  <i className="ri-close-circle-line" /> Cancel
                                </button>
                              )}
                              <button onClick={() => handlePrintEWB(ewb)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer">
                                <i className="ri-printer-line" /> Print
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Detail Slide-Over */}
        {showDetailSlideOver && selectedEWB && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowDetailSlideOver(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-lg overflow-y-auto">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">E-Way Bill — {selectedEWB.ewbNumber}</h2>
                <button onClick={() => setShowDetailSlideOver(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-lg text-gray-500" />
                </button>
              </div>
              <div className="px-5 py-4 space-y-5">
                {/* EWB Info */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">EWB Info</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">EWB Number</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 font-mono font-semibold">{selectedEWB.ewbNumber}</span>
                        <button onClick={() => handleCopyEWB(selectedEWB.ewbNumber)} className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 cursor-pointer" title="Copy">
                          <i className="ri-file-copy-line text-xs" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between"><span className="text-gray-500">Generated</span><span className="text-gray-900">{formatDateTime(selectedEWB.generatedDate)}</span></div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Valid Until</span>
                      <span className="text-gray-900">{formatDateTime(selectedEWB.validUntil)}{(selectedEWB.status === 'ACTIVE' || selectedEWB.status === 'EXTENDED') && <span className="ml-1 text-gray-400">({timeRemaining(selectedEWB.validUntil).text})</span>}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(selectedEWB.status)}`}>{getStatusLabel(selectedEWB.status)}</span>
                    </div>
                    {selectedEWB.cancelledAt && (
                      <>
                        <div className="flex items-center justify-between"><span className="text-gray-500">Cancelled At</span><span className="text-gray-900">{formatDateTime(selectedEWB.cancelledAt)}</span></div>
                        <div className="flex items-center justify-between"><span className="text-gray-500">Cancel Reason</span><span className="text-red-600">{selectedEWB.cancelReason}</span></div>
                      </>
                    )}
                  </div>
                </section>

                {/* Party Details */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Party Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'From', name: selectedEWB.supplierName, gstin: selectedEWB.fromGSTIN, addr: selectedEWB.fromAddress },
                      { label: 'To',   name: selectedEWB.customerName, gstin: selectedEWB.toGSTIN,   addr: selectedEWB.toAddress },
                    ].map(p => (
                      <div key={p.label} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1">
                        <p className="text-gray-500 font-medium">{p.label}</p>
                        <p className="text-gray-900 font-semibold">{p.name}</p>
                        <p className="text-gray-600">GSTIN: {p.gstin}</p>
                        <p className="text-gray-600 leading-relaxed">{p.addr}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Transport */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Transport Details</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-3 text-xs">
                    <div><span className="text-gray-500">Mode</span><div className="text-gray-900 font-medium mt-0.5">{getVehicleTypeLabel(selectedEWB.transportMode || selectedEWB.vehicleType)}</div></div>
                    <div><span className="text-gray-500">Distance</span><div className="text-gray-900 font-medium mt-0.5">{selectedEWB.distanceKm && Number(selectedEWB.distanceKm) > 0 ? `${selectedEWB.distanceKm} km` : '100 km'}</div></div>
                    <div>
                      <span className="text-gray-500">Vehicle Number</span>
                      <div className="text-gray-900 font-medium mt-0.5 font-mono">
                        {selectedEWB.vehicleNumber ? (
                          <span className="text-indigo-600 font-bold">{selectedEWB.vehicleNumber}</span>
                        ) : (
                          <span className="text-amber-600 italic">Not Assigned (Part-A Only)</span>
                        )}
                      </div>
                    </div>
                    <div><span className="text-gray-500">Transporter ID</span><div className="text-gray-900 font-medium mt-0.5">{selectedEWB.transporterId || '—'}</div></div>
                    <div className="col-span-2"><span className="text-gray-500">Transporter</span><div className="text-gray-900 font-medium mt-0.5">{selectedEWB.transporterName || '—'}</div></div>
                  </div>
                </section>

                {/* Value Summary */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Value Summary</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-xs">
                    <div className="flex items-center justify-between"><span className="text-gray-500">Total Taxable Value</span><span className="text-gray-900 font-medium">{formatINR(selectedEWB.totalTaxableValue)}</span></div>
                    {Number(selectedEWB.cgst) > 0 && <div className="flex items-center justify-between"><span className="text-gray-500">CGST</span><span>{formatINR(selectedEWB.cgst)}</span></div>}
                    {Number(selectedEWB.sgst) > 0 && <div className="flex items-center justify-between"><span className="text-gray-500">SGST</span><span>{formatINR(selectedEWB.sgst)}</span></div>}
                    {Number(selectedEWB.igst) > 0 && <div className="flex items-center justify-between"><span className="text-gray-500">IGST</span><span>{formatINR(selectedEWB.igst)}</span></div>}
                    <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
                      <span className="text-gray-900 font-semibold">Total Invoice Value</span>
                      <span className="text-gray-900 font-semibold">{formatINR(selectedEWB.totalValue)}</span>
                    </div>
                  </div>
                </section>

                <div className="flex flex-wrap items-center gap-2 pt-2 pb-4">
                  <button
                    onClick={() => {
                      setLookupEwbNo(selectedEWB.ewbNumber);
                      setLookupSupplierGstin(selectedEWB.fromGSTIN);
                      setShowLookupModal(true);
                    }}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shield-check-line" /> Verify from NIC
                  </button>
                  <button onClick={() => handlePrintEWB(selectedEWB)} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 cursor-pointer whitespace-nowrap">
                    <i className="ri-printer-line" /> Print EWB
                  </button>
                  {(selectedEWB.status === 'ACTIVE' || selectedEWB.status === 'EXTENDED') && (
                    <>
                      <button
                        onClick={() => { setShowDetailSlideOver(false); setUpdateVehicleTarget(selectedEWB); }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-truck-line" /> Update Vehicle
                      </button>
                      <button
                        onClick={() => { setShowDetailSlideOver(false); setUpdateTransporterTarget(selectedEWB); }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-user-shared-line" /> Update Transporter
                      </button>
                      <button
                        onClick={() => { setShowDetailSlideOver(false); setExtendTarget(selectedEWB); }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line" /> Extend Validity
                      </button>
                    </>
                  )}
                  {canCancel(selectedEWB) && (
                    <button onClick={() => { setShowDetailSlideOver(false); setTimeout(() => handleCancelClick(selectedEWB), 100); }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap">
                      <i className="ri-close-circle-line" /> Cancel EWB
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generate Slide-Over */}
        {showGenerateSlideOver && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowGenerateSlideOver(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-lg overflow-y-auto">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Generate E-Way Bill</h2>
                <button onClick={() => setShowGenerateSlideOver(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-lg text-gray-500" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-5">
                {/* Active Warehouse Context Info */}
                {selectedWarehouseName ? (
                  <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl px-3.5 py-2.5 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 text-slate-700">
                      <i className="ri-building-4-line text-amber-600 text-base" />
                      <div>
                        <span className="text-[10px] uppercase font-bold text-amber-800/80 block">Active Dispatch Warehouse</span>
                        <span className="text-xs font-bold text-slate-900">{selectedWarehouseName}</span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Source Type */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Source Document</h4>
                  <div className="flex gap-2">
                    {(['invoice', 'challan'] as const).map(t => (
                      <button key={t} onClick={() => { setGenSourceType(t); setGenSelectedInvoice(''); }}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg border cursor-pointer whitespace-nowrap transition-all ${genSourceType === t ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                        {t === 'invoice' ? 'Invoice' : 'Challan'}
                      </button>
                    ))}
                  </div>
                </section>

                {/* Invoice/Challan Search */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Search & Select {genSourceType === 'invoice' ? 'Invoice (Without E-Way Bill)' : 'Challan'}</h4>
                  <div className="relative">
                    <div className="relative flex items-center">
                      <i className="ri-search-line absolute left-3 text-gray-400 text-sm" />
                      <input type="text" value={docSearchQuery || genSelectedInvoice}
                        onFocus={() => setDocDropdownOpen(true)}
                        onChange={e => { setDocSearchQuery(e.target.value); setGenSelectedInvoice(e.target.value); setDocDropdownOpen(true); }}
                        placeholder={`Type ${genSourceType === 'invoice' ? 'invoice' : 'challan'} number or customer...`}
                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] bg-white" />
                      {isSearchingDocs ? <i className="ri-loader-4-line animate-spin absolute right-3 text-indigo-600 text-sm" /> : <i className="ri-arrow-down-s-line absolute right-3 text-gray-400 text-base" />}
                    </div>

                    {docDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-y-auto z-50 divide-y divide-gray-100">
                        {genSourceType === 'invoice' ? (
                          <>
                            {fetchedInvoices.length > 0 && <div className="bg-slate-50 px-3 py-1 text-[10px] font-bold text-slate-500 uppercase">Live Invoices (Pending EWB)</div>}
                            {fetchedInvoices.map(inv => {
                              const no    = inv.invoiceNo || inv.invoice_number || inv.billNo;
                              const name  = inv.partyName || inv.customer_name || inv.party_name;
                              const total = Number(inv.grandTotal || inv.total_amount || 0);
                              const gstin = inv.customerGstin || inv.customer_gstin || inv.gstin || '';
                              const addr  = inv.billingAddress || inv.billing_address || inv.address || '';
                              return (
                                <div key={inv.id || no} onClick={() => {
                                  setGenSelectedInvoice(no); setDocSearchQuery(no);
                                  setGenToGSTIN(gstin); setGenToAddress(addr);
                                  setSelectedInvoiceFull(null); // reset so useMemo refetches
                                  // ✅ Auto-fill pincode, stateCode, place
                                  const pc = extractPincode(addr) || Number(inv.billingPincode || inv.pincode || 0);
                                  const sc = getStateCodeFromGSTIN(gstin) || Number(inv.stateCode || inv.state_code || 0);
                                  const parts = addr.split(',').map((s: string) => s.trim()).filter(Boolean);
                                  setGenToPincode(pc ? String(pc) : '');
                                  setGenToStateCode(sc ? String(sc) : '');
                                  setGenToPlace(extractCity(addr));
                                  if (!genDistance) setGenDistance(sc === 27 ? '120' : '450');
                                  setDocDropdownOpen(false);
                                  // ✅ Fetch full invoice detail with items
                                  if (inv.id) fetchInvoiceDetail(inv.id);
                                }} className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer flex items-center justify-between">
                                  <div><p className="font-semibold text-slate-900">{no}</p><p className="text-[#64748b]">{name}</p></div>
                                  <span className="font-bold text-indigo-600">{formatINR(total)}</span>
                                </div>
                              );
                            })}
                            <div className="bg-slate-50 px-3 py-1 text-[10px] font-bold text-slate-500 uppercase">Reference Invoices</div>
                            {mockSalesInvoices
                              .filter(si => {
                                if (si.status === 'CANCELLED') return false;
                                if (si.grandTotal <= 50000) return false; // E-Way Bill not required for <= ₹50,000
                                if (eWayBillsList.some(e => e.status !== 'CANCELLED' && (e.invoiceId === si.id || e.invoiceNumber === si.billNo))) return false;
                                if (docSearchQuery && !si.billNo.toLowerCase().includes(docSearchQuery.toLowerCase()) && !si.partyName.toLowerCase().includes(docSearchQuery.toLowerCase())) return false;
                                return true;
                              })
                              .map(si => (
                                <div key={si.id} onClick={() => {
                                  setGenSelectedInvoice(si.billNo); setDocSearchQuery(si.billNo);
                                  setGenToGSTIN(si.customerGstin || ''); setGenToAddress(si.billingAddress);
                                  const pc = extractPincode(si.billingAddress);
                                  const sc = getStateCodeFromGSTIN(si.customerGstin || '');
                                  setGenToPincode(pc ? String(pc) : ''); setGenToStateCode(sc ? String(sc) : '');
                                  if (!genDistance) setGenDistance(sc === 27 ? '120' : '450');
                                  setDocDropdownOpen(false);
                                }} className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer flex items-center justify-between">
                                  <div><p className="font-semibold text-slate-900">{si.billNo}</p><p className="text-[#64748b]">{si.partyName}</p></div>
                                  <span className="font-bold text-slate-700">{formatINR(si.grandTotal)}</span>
                                </div>
                              ))}
                          </>
                        ) : (
                          fetchedChallans.length === 0
                            ? <div className="p-4 text-center text-xs text-slate-400">No challans found</div>
                            : fetchedChallans.map(c => {
                                const no    = c.challanNumber || c.challan_number || 'Challan';
                                const name  = c.customerName  || c.partyName     || 'Customer';
                                const total = Number(c.grandTotal ?? c.totalValue ?? 0);
                                const gstin = c.customerGstin || c.toGSTIN || '';
                                const addr  = c.deliveryAddress || c.toAddress || '';
                                return (
                                  <div key={c.id || no} onClick={() => {
                                    setGenSelectedInvoice(no); setDocSearchQuery(no);
                                    setGenToGSTIN(gstin); setGenToAddress(addr);
                                    setSelectedInvoiceFull(null);
                                    const pc = extractPincode(addr);
                                    const sc = getStateCodeFromGSTIN(gstin);
                                    setGenToPincode(pc ? String(pc) : ''); setGenToStateCode(sc ? String(sc) : '');
                                    if (!genDistance) setGenDistance(sc === 27 ? '120' : '450');
                                    setDocDropdownOpen(false);
                                    // ✅ Fetch full challan detail with items + HSN
                                    if (c.id) fetchChallanDetail(c.id);
                                  }} className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer flex items-center justify-between">
                                    <div><p className="font-semibold text-slate-900">{no}</p><p className="text-[#64748b]">{name}</p></div>
                                    <span className="font-bold text-indigo-600">{formatINR(total)}</span>
                                  </div>
                                );
                              })
                        )}
                      </div>
                    )}
                  </div>

                  {selectedInvoiceData && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-1.5">
                      <div className="flex justify-between"><span className="text-gray-500">Party</span><span className="font-medium">{selectedInvoiceData.partyName}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">GSTIN</span><span>{selectedInvoiceData.customerGstin || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Value</span><span className="font-medium">{formatINR(selectedInvoiceData.grandTotal)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Pincode</span><span>{genToPincode || selectedInvoiceData.pincode || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">State Code</span><span>{genToStateCode || selectedInvoiceData.stateCode || '—'}</span></div>
                    </div>
                  )}
                </section>

                {/* ✅ Manual override fields for pincode / state / place */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Customer Location</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Pincode <span className="text-red-500">*</span></label>
                      <input type="text" value={genToPincode} onChange={e => setGenToPincode(e.target.value)} placeholder="e.g. 400001" maxLength={6}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">State Code <span className="text-red-500">*</span></label>
                      <input type="text" value={genToStateCode} onChange={e => setGenToStateCode(e.target.value)} placeholder="e.g. 27"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Place / City</label>
                      <input type="text" value={genToPlace} onChange={e => setGenToPlace(e.target.value)} placeholder="e.g. Mumbai"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">Auto-filled from invoice — override if needed.</p>
                </section>

                {/* Transport Details */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Transport Details</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Mode</label>
                      <select value={genTransportMode} onChange={e => setGenTransportMode(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white">
                        <option value="1">Road</option>
                        <option value="2">Rail</option>
                        <option value="3">Air</option>
                        <option value="4">Ship</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Vehicle Number {genTransportMode === '1' && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="text"
                        value={genVehicleNumber}
                        onChange={e => setGenVehicleNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="e.g. DL01AB1234"
                        required={genTransportMode === '1'}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] uppercase font-mono font-medium"
                      />
                      <p className="mt-1 text-[11px] text-gray-400">
                        {genTransportMode === '1' ? 'Mandatory for Road transport (Part-B).' : 'Optional for Rail/Air/Ship (use doc details).'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Transporter Name</label>
                      <input type="text" value={genTransporterName} onChange={e => setGenTransporterName(e.target.value)} placeholder="e.g. Fast Logistics Pvt Ltd"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Transporter ID</label>
                      <input type="text" value={genTransporterId} onChange={e => setGenTransporterId(e.target.value)} placeholder="Transporter GSTIN or TRANSIN"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-gray-700">Approximate Distance (km)</label>
                        {distanceCalc && (
                          <span className="text-[11px] font-semibold text-indigo-600 flex items-center gap-1">
                            <i className="ri-map-pin-range-line" />
                            {distanceCalc.fromName && distanceCalc.toName ? `${distanceCalc.fromName} → ${distanceCalc.toName}` : 'Auto-calculated'}
                          </span>
                        )}
                      </div>
                      <div className="relative flex items-center">
                        <input
                          type="number"
                          value={genDistance}
                          onChange={e => setGenDistance(e.target.value)}
                          placeholder="e.g. 150"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] pr-20"
                        />
                        {distanceCalc && (
                          <button
                            type="button"
                            onClick={() => setGenDistance(String(distanceCalc.distanceKm))}
                            className="absolute right-2 px-2 py-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 cursor-pointer"
                            title="Recalculate with Haversine formula"
                          >
                            Recalculate
                          </button>
                        )}
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[11px]">
                        <span className="text-gray-400">⚡ Auto-calculated via Haversine distance formula.</span>
                        {genDistance && !isNaN(parseInt(genDistance)) && (
                          <span className="text-[#4f46e5] font-semibold">
                            Validity: {estimatedValidityDays} day{estimatedValidityDays > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Generate Button */}
                <div className="pt-2 pb-4">
                  <button
                    onClick={handleGenerateEWB}
                    disabled={!genSelectedInvoice || isGenerating || (genTransportMode === '1' && !genVehicleNumber.trim())}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isGenerating ? <><i className="ri-loader-4-line animate-spin" /> Generating...</> : <><i className="ri-check-line" /> Generate EWB</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Cancel Modal — NIC reason codes */}
        {showCancelModal && cancelTarget && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowCancelModal(false)} />
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Cancel E-Way Bill {cancelTarget.ewbNumber}?</h3>
              <p className="text-sm text-gray-500 mb-4">EWB can only be cancelled within 24 hours of generation.</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Cancellation Reason <span className="text-red-500">*</span></label>
                  <select value={cancelRsnCode} onChange={e => setCancelRsnCode(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white">
                    <option value={0}>Select reason...</option>
                    {cancelReasons.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                  </select>
                </div>
                {cancelRsnCode === 4 && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Specify Reason</label>
                    <textarea value={cancelOtherRemark} onChange={e => setCancelOtherRemark(e.target.value)} rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] resize-none" placeholder="Enter reason..." />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 mt-5">
                <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">Go Back</button>
                <button onClick={handleConfirmCancel} disabled={!cancelRsnCode || (cancelRsnCode === 4 && !cancelOtherRemark.trim()) || isCancelling}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {isCancelling ? 'Cancelling...' : 'Confirm Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {extendTarget            && <ExtendSlideOver ewb={extendTarget} onClose={handleExtendClosed} onExtended={handleExtended} />}
        {updateVehicleTarget     && <UpdateVehicleSlideOver ewb={updateVehicleTarget} onClose={() => setUpdateVehicleTarget(null)} onUpdated={handleVehicleUpdated} />}
        {updateTransporterTarget && <UpdateTransporterModal ewb={updateTransporterTarget} onClose={() => setUpdateTransporterTarget(null)} onUpdated={handleTransporterUpdated} />}
        {showBulkModal           && <BulkGenerateModal onClose={() => setShowBulkModal(false)} onGenerated={handleBulkGenerated} />}
        {printEWB                && <PrintEWayBillView ewb={printEWB} onClose={() => setPrintEWB(null)} />}
        <GetEWayBillDetailsModal
          isOpen={showLookupModal}
          onClose={() => setShowLookupModal(false)}
          initialEwbNo={lookupEwbNo}
          initialSupplierGstin={lookupSupplierGstin}
        />

      </div>
    </AppLayout>
  );
}